import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import authConfig from '../../config/auth';
import SessionHelper from '../Helper/SessionHelper';
import { AUTH_USER } from '../../constants/ActionTypes';
import { Button, TtsSwirl } from '../redesign';

function Callback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(searchParams.get('error_description') || 'Authentication failed');
      return;
    }

    if (!code) {
      setError('No authorization code received');
      return;
    }

    // Immediately strip the code from the URL so browser back-button can't replay it
    // (OAuth authorization codes are single-use; replaying causes invalid_grant)
    window.history.replaceState({}, '', '/callback');

    exchangeCodeForTokens(code);
  }, []);

  async function exchangeCodeForTokens(code) {
    let cognitoRefreshToken = null;

    try {
      // Clear any stale TTS auth tokens from previous sessions so getToken()
      // returns the local JWT (cookie) instead of an expired Cognito token
      SessionHelper.clearTtsAuthTokens();

      // Exchange code for tokens via TTS Auth
      const tokenResponse = await fetch(`${authConfig.ttsAuthUrl}/api/v1/auth/token-exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          redirectUri: authConfig.callbackUrl,
          clientId: authConfig.clientId,
        }),
      });

      if (!tokenResponse.ok) {
        throw new Error('Token exchange failed');
      }

      const tokens = await tokenResponse.json();
      const idToken = tokens.data?.idToken || tokens.idToken;
      // Capture refresh token early so we can revoke the Cognito session on failure
      cognitoRefreshToken = tokens.data?.refreshToken || tokens.refreshToken;

      if (!idToken) {
        throw new Error('No ID token in response');
      }

      // Send ID token to APIv2 for server-side validation and admin lookup
      const ssoResponse = await fetch(`${authConfig.apiV2Url}/api/admin/sso-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken }),
      });

      if (!ssoResponse.ok) {
        const errorData = await ssoResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to authenticate. You may not have CMS access.');
      }

      const authData = await ssoResponse.json();

      if (!authData.access_token) {
        throw new Error('No access token in SSO response');
      }

      // Store the local API JWT (used for all CMS API calls via auth middleware)
      SessionHelper.beginLogin(SessionHelper.SESSION_SSO);
      SessionHelper.setToken(authData.access_token);

      // Store the Cognito refresh token so we can silently refresh the local JWT
      // when it expires (we don't store the Cognito access token — only the refresh token)
      if (cognitoRefreshToken) {
        SessionHelper.setTtsRefreshToken(cognitoRefreshToken);
      }

      // Decode the local JWT to get admin data for session
      // Convert base64url to base64, then decode handling UTF-8 multi-byte characters
      const base64Url = authData.access_token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(decodeURIComponent(escape(atob(base64))));
      const userData = {
        admin_id: payload.admin?.id,
        admin_email: payload.admin?.email,
        admin_name: payload.admin?.name,
        admin_image: payload.admin?.image || '',
        permissions: payload.admin?.permissions || [],
        admin_roles: payload.admin?.roles || [],
        // SSO sessions are federated — the email is owned by the
        // identity provider (Cognito + its upstream IdP), so the
        // CMS shouldn't let the user edit it from My Account.
        // The password path (authActions.emailLogin) sets this to
        // false explicitly so the flag's intent is unambiguous.
        is_federated: true,
      };

      SessionHelper.setUserData(userData);

      dispatch({ type: AUTH_USER });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Auth callback error:', err);

      // If we got Cognito tokens before the failure, revoke the Cognito session
      // and redirect through its logout URL to clear the browser session cookie.
      // Without this, Cognito reuses the stale session (and its old claims) on
      // the next login attempt — e.g. after an admin's scope is changed server-side.
      if (cognitoRefreshToken) {
        const logoutUrl = await SessionHelper.revokeCognitoSession(
          cognitoRefreshToken,
          window.location.origin,
        );
        if (logoutUrl) {
          window.location.href = logoutUrl;
          return;
        }
      }

      setError(err.message || 'Authentication failed');
    }
  }

  return (
    <div
      className="bg-bg min-h-screen flex items-center justify-center"
      style={{ padding: '24px 16px' }}
    >
      <CallbackCard error={error} onBackToLogin={() => navigate('/login')} />
      <style>{`
        @keyframes ttsSwirlPulse {
          0%, 100% { transform: scale(1);    opacity: 1;   }
          50%      { transform: scale(1.06); opacity: 0.85;}
        }
      `}</style>
    </div>
  );
}

/* ─── Card shown while exchanging the OAuth code or surfacing an
 *  error from that exchange. Cream/sand brand palette, centered
 *  card, animated swirl logo. */

function CallbackCard({ error, onBackToLogin }) {
  return (
    <div
      className="bg-surface border border-sand"
      style={{
        width: '100%',
        maxWidth: 420,
        borderRadius: 14,
        boxShadow: 'var(--login-card-shadow-sm)',
        padding: '36px 32px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          margin: '0 auto 18px',
          animation: error ? 'none' : 'ttsSwirlPulse 1.6s ease-in-out infinite',
          opacity: error ? 0.45 : 1,
          filter: error ? 'grayscale(0.6)' : undefined,
          transition: 'opacity 300ms ease, filter 300ms ease',
        }}
      >
        <TtsSwirl style={{ width: '100%', height: '100%' }} />
      </div>

      <div
        className="font-condensed font-bold uppercase"
        style={{
          fontSize: 11,
          letterSpacing: '0.12em',
          color: error ? '#A5524B' : '#5C7E72',
          marginBottom: 6,
        }}
      >
        {error ? 'Authentication error' : 'Signing you in'}
      </div>

      <h1
        className="text-navy"
        style={{
          fontSize: 22,
          fontWeight: 700,
          margin: '0 0 10px',
          letterSpacing: '-0.01em',
          lineHeight: 1.25,
        }}
      >
        {error ? "We couldn't sign you in" : 'Hang tight…'}
      </h1>

      <p
        className="text-text-muted"
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          margin: '0 0 22px',
          // Long error messages from the server can be a paragraph
          // long — let them wrap naturally.
          wordBreak: 'break-word',
        }}
      >
        {error || 'Verifying your identity provider — this only takes a second.'}
      </p>

      {error && (
        <Button variant="coral" onClick={onBackToLogin}>
          Back to login
        </Button>
      )}
    </div>
  );
}

export default Callback;
